import axios, { AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios'
import store from '@/store'
import { LoginRoute } from '@/api/Auth/auth.api'
import { displayInSnack } from '@/utils/functions'

export const axiosBase = axios.create({
  baseURL: process.env.VUE_APP_BASE_URL || '/',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

export const axiosApi = axios.create({
  baseURL: (process.env.VUE_APP_BASE_URL || '/') + 'api/',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
})

const MAX_REQUESTS_COUNT = Number(process.env.VUE_APP_MAX_REQUESTS_COUNT) || 5
const INTERVAL_MS = Number(process.env.VUE_APP_INTERVAL_MS) || 10
let PENDING_REQUESTS = 0

axiosApi.interceptors.request.use(
  config => {
    return new Promise(resolve => {
      const interval = setInterval(() => {
        if (PENDING_REQUESTS < MAX_REQUESTS_COUNT) {
          PENDING_REQUESTS++
          clearInterval(interval)
          const token = store.state.accessToken
          if (token) {
            if (!config.headers)
              config.headers = {
                Authorization: 'Bearer ' + token,
              }
            else config.headers.Authorization = 'Bearer ' + token
          }
          resolve(config)
        }
      }, INTERVAL_MS)
    })
  },
  err => {
    Promise.reject(err)
  },
)

interface CanRetry extends AxiosRequestConfig {
  _retry?: boolean
}

axiosApi.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    PENDING_REQUESTS = Math.max(0, PENDING_REQUESTS - 1)
    return response
  },
  async (error: AxiosError): Promise<AxiosError | AxiosResponse> => {
    PENDING_REQUESTS = Math.max(0, PENDING_REQUESTS - 1)
    if (
      error.config &&
      error.config.headers &&
      error.response &&
      error.response.status === 401 &&
      !(error.config as CanRetry)._retry
    ) {
      ;(error.config as CanRetry)._retry = true
      try {
        const access = await store.dispatch('refreshToken')
        error.config.headers.Authorization = `Bearer ${access}`
        return axiosApi(error.config)
      } catch (err) {
        return Promise.reject(err)
      }
    }
    displayInSnack(error)
    return Promise.reject(error)
  },
)

axiosBase.interceptors.response.use(undefined, (err: AxiosError) => {
  if (
    err.config &&
    err.response &&
    err.response.status === 401 &&
    err.response.config.url !== LoginRoute &&
    store.state.initialized
  ) {
    store.dispatch('LOGOUT_USER')
  }
})
